import { Providers } from "@microsoft/mgt-element";
import { Msal2Provider, PromptType } from "@microsoft/mgt-msal2-provider";
import { EventType, LogLevel } from '@azure/msal-browser';
import { isValidEmail, getStoredRedirectId, getStoredLink } from "../utils";

type TokenSubscriberCallback = (token: string) => void;
type UnsubscribeFunction = () => void;

class AuthService {
  private static instance: AuthService;

  private static readonly REFRESH_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
  private static readonly INACTIVITY_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
  private static readonly INTERACTION_REQUIRED_ERROR = "InteractionRequiredAuthError";
  
  private tokenSubscribers: TokenSubscriberCallback[] = [];
  private lastActivityTime: number = Date.now();
  private refreshIntervalId: number | null = null;
  private activityCheckIntervalId: number | null = null;
  private debounceTimeout: NodeJS.Timeout | undefined = undefined;
  private accessToken: string = "";
  
  private constructor() {
    localStorage.setItem('last_active_timestamp', Date.now().toString());
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }
  
  /**
   * Initialize the authentication manager
   */
  public async initialize(): Promise<void> {
    this.configureMsalProvider();

    const provider = Providers.globalProvider as Msal2Provider;
    const msalInstance = provider.publicClientApplication;

    try {
      await msalInstance.handleRedirectPromise();
    } catch (err) {
      console.error("Error handling redirect promise:", err);
    }

    this.setupEventHandlers();
    this.setupTokenRefresh();
    this.setupActivityTracking();
    this.checkPageReload();
    this.setupOnlineListener();
  }
  
  /**
   * Get the last activity time
   */
  public getLastActivityTime(): number {
    return this.lastActivityTime;
  }
  
  /**
   * Subscribe to token changes
   * @param callback Function to call when token changes
   * @returns Function to unsubscribe
   */
  public subscribeToTokenChanges(callback: TokenSubscriberCallback): UnsubscribeFunction {
    this.tokenSubscribers.push(callback);
    return (): void => {
      const index = this.tokenSubscribers.indexOf(callback);
      if (index !== -1) {
        this.tokenSubscribers.splice(index, 1);
      }
    };
  }

  /**
   * Clean the list of subscribers
   */
  public cleanSubscribers(): void {
    this.tokenSubscribers = [];
  }
  
  /**
   * Manually trigger a token refresh
   */
  public async refreshToken(): Promise<void> {
    await this.silentTokenRefresh();
  }

  public async getAccessToken(): Promise<string> {
    if (!this.accessToken) {
      this.accessToken = await Providers.globalProvider.getAccessToken();
    }

    return this.accessToken;
  }
  
  /**
   * Check for inactivity period and refresh token if needed
   */
  private checkInactivityPeriod(): void {
    const lastActiveTimestamp = localStorage.getItem('last_active_timestamp');
    const now = Date.now();
    
    // Update the activity timestamp
    this.lastActivityTime = now;
    localStorage.setItem('last_active_timestamp', now.toString());
    
    if (lastActiveTimestamp) {
      const timeSinceLastActive = now - parseInt(lastActiveTimestamp);
      if (timeSinceLastActive > AuthService.INACTIVITY_THRESHOLD_MS) {
        console.log('Detected return after inactivity, refreshing token');
        this.silentTokenRefresh();
      }
    } else {
      // No timestamp found, refresh to be safe
      this.silentTokenRefresh();
    }
  }
  
  /**
   * Handle user activity events
   */
  private handleUserActivity(event?: Event): void {
    const now = Date.now();
    const previousActivity = this.lastActivityTime;
    this.lastActivityTime = now;
    if (now - previousActivity > AuthService.INACTIVITY_THRESHOLD_MS) {
      this.checkInactivityPeriod();
    }
  }
  
  /**
   * Notify all token subscribers
   */
  private notifyTokenSubscribers(token: string): void {
    this.tokenSubscribers.forEach(callback => {
      try {
        callback(token);
      } catch (error) {
        console.error('Error in token subscriber callback:', error);
      }
    });
  }
  
  /**
   * Perform a silent token refresh
   */
  public async silentTokenRefresh(): Promise<void> {
    const provider = Providers.globalProvider as Msal2Provider;
    const msalInstance = provider?.publicClientApplication;

    if (!msalInstance) return;
    
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return;

      const activeAccount = msalInstance.getActiveAccount();
      if (!activeAccount) {
        msalInstance.setActiveAccount(accounts[0]);
      }
      
      // Use the provider's built-in method to refresh token
      const token = await provider.getAccessToken();
      console.log("Token silently refreshed");

      this.notifyTokenSubscribers(token);
    } catch (error: any) {
      // If interaction is required (e.g., consent needed or password expired)
      if (error && error.name === AuthService.INTERACTION_REQUIRED_ERROR) {
        console.log("Interaction required for token refresh");
        // Only redirect to login if we're not in the middle of another operation
        const isRedirectInProgress = msalInstance.getActiveAccount() !== null;
        if (!isRedirectInProgress) {
          // Check if the user is currently on the page before redirecting
          if (document.visibilityState === "visible") {
            provider.login().catch(err => console.error('Error during login redirect:', err));
          }
        }
      } else {
        console.error("Error during silent token refresh:", error);
      }
    }
  }
  
  /**
   * Configure the MSAL provider
   */
  private configureMsalProvider(): void {
    Providers.globalProvider = new Msal2Provider({
      clientId: process.env.REACT_APP_CLIENT_ID!,
      authority: process.env.REACT_APP_AUTHORITY!,
      redirectUri: process.env.REACT_APP_REDIRECT_URI!,
      isMultiAccountEnabled: false,
      prompt: this.getPromptType(),
      loginHint: localStorage.getItem('loginHint') || undefined,
      options: this.getMsalOptions()
    });
  }
  
  /**
   * Get MSAL options
   */
  private getMsalOptions(): any {
    return {
      auth: {
        clientId: process.env.REACT_APP_CLIENT_ID!,
        redirectUri: process.env.REACT_APP_REDIRECT_URI!,
        authority: process.env.REACT_APP_AUTHORITY!,
        postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI!,
        navigateToLoginRequestUrl: true,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true
      },
      system: {
        loggerOptions: {
          loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
            if (!containsPii) {
              switch (level) {
                case LogLevel.Error: console.error(message); break;
                case LogLevel.Warning: console.warn(message); break;
                case LogLevel.Info: console.info(message); break;
                case LogLevel.Verbose: console.debug(message); break;
              }
            }
          },
          logLevel: LogLevel.Warning,
          piiLoggingEnabled: false
        }
      }
    };
  }
  
  /**
   * Get the prompt type based on parameters
   */
  private getPromptType(): PromptType | undefined {  
    const params = new URLSearchParams(window.location.search);
    
    let loginHint = localStorage.getItem("loginHint")?.toLowerCase() ?? 
                   params.get("loginHint")?.toLowerCase();
  
    if (loginHint) {
      localStorage.setItem("loginHint", loginHint);
    }
  
    // Check for previous login hint
    const hasValidLoginHint = localStorage.getItem("loginHint") && 
                             isValidEmail(localStorage.getItem("loginHint"));
  
    return hasValidLoginHint 
      ? PromptType.LOGIN 
      : PromptType.SELECT_ACCOUNT;
  }
  
  /**
   * Setup event handlers for the MSAL provider
   */
  private setupEventHandlers(): void {
    const provider = Providers.globalProvider as Msal2Provider;
  
    provider.publicClientApplication!.addEventCallback((event: any) => {
      if (event.eventType === EventType.SSO_SILENT_FAILURE) {
        if (event.error.name === AuthService.INTERACTION_REQUIRED_ERROR) {
          let redirect_id = getStoredRedirectId();
          let storedLink = getStoredLink();
  
          // We only want to trigger login if the user is using a link
          if (redirect_id || storedLink) {
            console.log('Interaction required, initiating login flow');
            provider.login().catch(error => console.error('Error during login:', error));
          }
        }
      }

      if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
        if (this.debounceTimeout) {
          clearTimeout(this.debounceTimeout);
        }
        this.debounceTimeout = setTimeout(() => {
          this.accessToken = event.payload.accessToken;
          this.notifyTokenSubscribers(this.accessToken!);
          this.debounceTimeout = undefined;
        }, 1000);
      }
    });
  }
  
  /**
   * Setup token refresh interval
   */
  private setupTokenRefresh(): void {
    this.refreshIntervalId = window.setInterval(
      () => this.silentTokenRefresh(),
      AuthService.REFRESH_INTERVAL_MS
    );
    
    document.addEventListener('visibilitychange', (event) => {
      if (document.visibilityState === 'visible') {
        this.checkInactivityPeriod();
      }
    });
  }
  
  /**
   * Setup activity tracking
   */
  private setupActivityTracking(): void {
    const boundHandleUserActivity = this.handleUserActivity.bind(this);
    
    ['click', 'keypress', 'mousemove', 'touchstart', 'scroll'].forEach(eventType => {
      document.addEventListener(eventType, boundHandleUserActivity, { passive: true });
    });
  
    this.activityCheckIntervalId = window.setInterval(() => {
      const inactiveTime = Date.now() - this.lastActivityTime;
      if (inactiveTime > AuthService.INACTIVITY_THRESHOLD_MS) {
        this.checkInactivityPeriod();
      }
    }, 60000);
  }
  
  /**
   * Check for page reload
   */
  private checkPageReload(): void {
    // Store timestamp before page unload
    window.addEventListener('beforeunload', () => {
      localStorage.setItem('last_active_timestamp', Date.now().toString());
    });
    
    // Check time difference on page load
    const lastActiveTimestamp = localStorage.getItem('last_active_timestamp');
    if (lastActiveTimestamp) {
      const timeSinceLastActive = Date.now() - parseInt(lastActiveTimestamp);
      if (timeSinceLastActive > AuthService.INACTIVITY_THRESHOLD_MS) {
        // If returning after long period, verify auth
        this.silentTokenRefresh();
      }
    }
    
    // Update the timestamp
    localStorage.setItem('last_active_timestamp', Date.now().toString());
  }
  
  /**
   * Setup online listener
   */
  private setupOnlineListener(): void {
    // Check authentication when coming back online (likely after sleep/hibernate)
    window.addEventListener('online', () => {
      console.log('Network connection restored, checking authentication');
      this.checkInactivityPeriod();
    });
  }
  
  /**
   * Clean up resources (intervals, event listeners)
   */
  public dispose(): void {
    if (this.refreshIntervalId !== null) {
      window.clearInterval(this.refreshIntervalId);
      this.refreshIntervalId = null;
    }
    
    if (this.activityCheckIntervalId !== null) {
      window.clearInterval(this.activityCheckIntervalId);
      this.activityCheckIntervalId = null;
    }
  }
}

export const authService = AuthService.getInstance();